import { useDeleteInstallation } from '@/features/suppliers/hooks/installations'
import useI18n from '@/shared/hooks/usei18n'
import { Alert, Button, GenericDialog } from '@/common'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'

export default function DeleteInstallationDialog({
  isOpen,
  close,
  installationName,
  partnershipId,
  installationId,
  onAfterLeave
}: {
  isOpen: boolean
  close: () => void
  installationName: string
  partnershipId: string
  installationId: string
  onAfterLeave?: () => void
}) {
  const { t } = useI18n()
  const { mutateAsync: deleteInstallation, isPending: isDeleting } = useDeleteInstallation(
    partnershipId,
    installationId
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onDeleteClick = useCallback(async () => {
    try {
      setErrorMessage(null)
      await deleteInstallation()
      toast.success(t('suppliers:supplierViewPage.dialogs.deleteInstallation.notifications.success'))
      close()
      if (onAfterLeave) {
        onAfterLeave()
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.message

      if (errorCode === 'INSTALLATION_HAS_IMPORTED_GOODS') {
        setErrorMessage(
          t('suppliers:supplierViewPage.dialogs.deleteInstallation.errors.installationHasImportedGoods')
        )
      } else {
        toast.error(t('suppliers:supplierViewPage.dialogs.deleteInstallation.notifications.error'))
      }
    }
  }, [deleteInstallation, close, t, onAfterLeave])

  const onClose = useCallback(() => {
    close()
    setErrorMessage(null)
  }, [close])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('suppliers:supplierViewPage.dialogs.deleteInstallation.title')}
      size="md"
    >
      <div className="space-y-4">
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <p>
          <Trans
            i18nKey="suppliers:supplierViewPage.dialogs.deleteInstallation.description"
            values={{ name: installationName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={onDeleteClick} isLoading={isDeleting}>
            {t('common:actions.delete')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
